import React, { useEffect, useRef, useState, useMemo, useCallback } from 'react';
import { Button, Pagination, Avatar, MonthYearPicker, LoadingSpinner } from 'components/common';
import ReactDOM from 'react-dom';
import { useDispatch, useSelector } from 'react-redux';
import { AgGridReact } from 'ag-grid-react';
import { FaSearch, FaTimes } from 'react-icons/fa';
import PropTypes from 'prop-types';
import dayjs from 'dayjs';
import {
  getEligibleBuildings,
  createSalesForecast,
  updateSalesForecast,
} from 'redux/crm/releaseForecastSlice';

// ── helpers ──────────────────────────────────────────────────────────────────

// Number of grace days at the start of each month during which the
// previous month's forecast is still the active reporting period.
const GRACE_DAYS = 7;

/**
 * Returns the effective reporting period ("MMM-YYYY") accounting for grace days.
 *   - Days 1–GRACE_DAYS of a new month → previous month still active
 *   - After GRACE_DAYS            → current month is active
 */
const getEffectivePeriod = () => {
  const today = dayjs();
  const base  = today.date() <= GRACE_DAYS ? today.subtract(1, 'month') : today;
  return base.format('MMM-YYYY');
};

const PAGE_SIZE_OPTIONS = [20, 50, 100, 200];
const DEFAULT_PER_PAGE  = 50;

function useDebounce(value, delay) {
  const [debounced, setDebounced] = useState(value);
  useEffect(() => {
    const t = setTimeout(() => setDebounced(value), delay);
    return () => clearTimeout(t);
  }, [value, delay]);
  return debounced;
}

// ── component ─────────────────────────────────────────────────────────────────

// defaultPeriod (page filter) is accepted but intentionally ignored — see the grace-period note below.
const SalesBuildingSelectModal = ({ show, onHide, onSuccess }) => {
  const dispatch       = useDispatch();
  const buildingsState = useSelector((state) => state.crm_release_forecast.buildingsData);
  const salesState     = useSelector((state) => state.crm_release_forecast.salesData);

  const gridRef     = useRef(null);

  // Always initialize to effectivePeriod regardless of defaultPeriod,
  // so the grace-period rule is enforced from the first render.
  const [period,         setPeriod]         = useState(() => getEffectivePeriod());
  const [generalFilter,  setGeneralFilter]  = useState('');
  const [page,           setPage]           = useState(1);
  const [perPage,        setPerPage]        = useState(DEFAULT_PER_PAGE);
  const [sortModel,      setSortModel]      = useState({ col: 'created_at', dir: 'desc' });

  const debouncedGeneralFilter = useDebounce(generalFilter, 250);

  const [selectionInfo, setSelectionInfo] = useState({ count: 0, projectCount: 0 });
  const [submitting,    setSubmitting]    = useState(false);
  const [isFetching,    setIsFetching]    = useState(false);

  // When modal opens, always enforce the effective period (grace-period logic).
  // defaultPeriod from the page filter is intentionally ignored here because
  // the page filter might show a future/wrong month.
  useEffect(() => {
    if (!show) return;
    setPeriod(getEffectivePeriod());
    setGeneralFilter('');
    setPage(1);
  }, [show]);

  // Lock body scroll while open
  useEffect(() => {
    document.body.style.overflow = show ? 'hidden' : '';
    return () => { document.body.style.overflow = ''; };
  }, [show]);

  // Reset page to 1 when search text changes (period change is handled above)
  useEffect(() => {
    setPage(1);
  }, [debouncedGeneralFilter]);

  // Show spinner immediately when any filter changes (but not when text is 1-2 chars — we skip that fetch)
  useEffect(() => {
    if (!show) return;
    const trimmed = debouncedGeneralFilter?.trim() ?? '';
    if (trimmed.length === 0 || trimmed.length > 2) setIsFetching(true);
  }, [show, page, perPage, period, debouncedGeneralFilter, sortModel]);

  // Server fetch — skip when filter text is 1-2 chars to reduce server load
  useEffect(() => {
    if (!show) return;

    const trimmed = debouncedGeneralFilter?.trim() ?? '';
    // 1-2 chars: user is still typing, don't fetch yet
    if (trimmed.length > 0 && trimmed.length <= 2) return;

    let cancelled = false;

    const params = { page, per_page: perPage, period, sort_col: sortModel.col, sort_dir: sortModel.dir };
    if (trimmed.length > 2) params.general_search = trimmed;

    dispatch(getEligibleBuildings(params)).finally(() => {
      if (!cancelled) setIsFetching(false);
    });

    return () => { cancelled = true; };
  }, [show, page, perPage, period, debouncedGeneralFilter, sortModel, dispatch]);

  // Clear selection when page data changes
  useEffect(() => {
    setSelectionInfo({ count: 0, projectCount: 0 });
  }, [buildingsState.allData]);

  // ── grid ─────────────────────────────────────────────────────────────────

  const rowData = useMemo(() => {
    const items = buildingsState.allData?.data || [];
    return items.map((item) => ({
      id:                  item.id,
      name:                item.name,
      j_number:            item.j_number,
      pj_location:         item.pj_location,
      est_released_weight: item.est_released_weight,
      building_status:     item.building_status?.name,
      project_id:          item.project?.id,
      project_name:        item.project?.name,
      project_status_name: item.project?.project_status_name,
      sales_incharge:      item.project?.sales_incharge,
      q_number:            item.project?.q_number,
      customer_name:       item.project?.customer_name,
      sales_unit_id:       item.project?.sales_unit_id,
      sales_unit_name:     item.project?.sales_unit_name,
      project_created_at:  item.project?.created_at || null,
    }));
  }, [buildingsState.allData]);

  // Sort project groups by newest project first — overrides AG Grid's default alphabetical group sort
  const initialGroupOrderComparator = useCallback((params) => {
    const aDate = params.nodeA.allLeafChildren?.[0]?.data?.project_created_at;
    const bDate = params.nodeB.allLeafChildren?.[0]?.data?.project_created_at;
    if (!aDate && !bDate) return 0;
    if (!aDate) return 1;
    if (!bDate) return -1;
    return new Date(bDate) - new Date(aDate); // descending: newest first
  }, []);

  const totalRows  = buildingsState.allData?.totalRows || 0;
  const totalPages = Math.ceil(totalRows / perPage) || 1;

  const columnDefs = useMemo(
    () => [
      {
        headerName: 'Building',
        field: 'name',
        minWidth: 140,
        flex: 1,
        sortable: true,
      },
      {
        headerName: 'Quote/Job number',
        field: 'j_number',
        width: 130,
        sortable: true,
        // Group rows show the project Q number; leaf rows show building j_number
        valueGetter: (p) => {
          if (p.node?.group) return p.node.allLeafChildren?.[0]?.data?.q_number || '';
          return p.data?.j_number || '';
        },
        cellStyle: (p) => p.node?.group ? { color: '#64748b', fontStyle: 'italic' } : {},
      },
      { headerName: 'Location', field: 'pj_location', width: 130, sortable: true },
      {
        headerName: 'Est. Weight (MT)',
        field: 'est_released_weight',
        width: 150,
        sortable: true,
        type: 'numericColumn',
        aggFunc: 'sum',
        // Parse to float so aggFunc:'sum' receives numbers, not strings
        valueGetter: (p) => {
          if (p.node?.group) return undefined;
          return parseFloat(p.data?.est_released_weight) || 0;
        },
        valueFormatter: (p) => {
          if (p.value == null || p.value === '') return '—';
          const num = Number(p.value);
          return isNaN(num) ? '—' : num.toFixed(2);
        },
        cellStyle: (p) => p.node?.group
          ? { textAlign: 'right', fontWeight: 600, color: '#1d4ed8' }
          : { textAlign: 'right' },
      },
      {
        headerName: 'Status',
        field: 'building_status',
        width: 180,
        cellRenderer: (p) => {
          if (p.node?.group) {
            const status = p.node.allLeafChildren?.[0]?.data?.project_status_name;
            return status ? (
              <span className="tw-inline-flex tw-items-center tw-px-2 tw-py-0.5 tw-rounded-sm tw-bg-blue-50 tw-text-blue-600 tw-text-xs tw-font-medium tw-border tw-border-blue-100">
                {status}
              </span>
            ) : null;
          }
          return p.value ? (
            <span className="tw-bg-blue-40 tw-text-blue-400 tw-text-xs tw-font-medium">{p.value}</span>
          ) : null;
        },
      },
      {
        headerName: 'Sales Incharge',
        field: 'sales_incharge',
        width: 150,
        cellRenderer: (p) => {
          if (!p.node?.group) return null;
          const incharge = p.node.allLeafChildren?.[0]?.data?.sales_incharge;
          if (!incharge) return null;
          const initials = incharge.split(' ').map((n) => n[0]).join('').slice(0, 2).toUpperCase();
          return (
            <div className="tw-flex tw-items-center tw-gap-1.5 tw-h-full">
              <Avatar initials={initials} size="xs" variant="primary" />
              <span className="tw-text-xs tw-font-medium tw-text-slate-700">{incharge}</span>
            </div>
          );
        },
      },
      { field: 'project_name',        rowGroup: true, hide: true },
      { field: 'project_status_name', hide: true },
      { field: 'sales_incharge',      hide: true },
      { field: 'q_number',            hide: true },
      { field: 'sales_unit_id',       hide: true },
      {
        headerName: 'Sales Unit',
        field: 'sales_unit_name',
        width: 150,
        cellRenderer: (p) => {
          if (!p.node?.group) return null;
          const salesUnit = p.node.allLeafChildren?.[0]?.data?.sales_unit_name;
          if (!salesUnit) return null;
          return salesUnit
        },
      },
    ],
    []
  );

  // Group header: project name only (status + incharge in their own columns)
  const GroupCellRenderer = useCallback((p) => {
    if (!p.node?.group) return <span>{p.value}</span>;
    const count = p.node.allLeafChildren?.length ?? 0;
    return (
      <span className="tw-font-semibold tw-text-slate-900">
        {p.value}
        <span className="tw-text-slate-400 tw-font-normal tw-ml-1 tw-text-xs">({count})</span>
      </span>
    );
  }, []);

  // autoGroupColumnDef: use GroupCellRenderer directly (not innerRenderer) for full React control
  const autoGroupColumnDef = useMemo(
    () => ({
      headerName: 'Project / Building',
      minWidth: 400,
      headerCheckboxSelection: true,
      cellRenderer: 'agGroupCellRenderer',
      cellRendererParams: {
        checkbox: true,
        innerRenderer: GroupCellRenderer,
      },
    }),
    [GroupCellRenderer]
  );

  const handleSortChanged = useCallback(() => {
    const sortState = gridRef.current?.api?.getColumnState() ?? [];
    const sorted = sortState.find((c) => c.sort);
    if (sorted) {
      setSortModel({ col: sorted.colId, dir: sorted.sort });
    } else {
      setSortModel({ col: 'created_at', dir: 'desc' });
    }
    setPage(1);
  }, []);

  const handleSelectionChanged = useCallback(() => {
    const nodes  = gridRef.current?.api?.getSelectedNodes() ?? [];
    const leaves = nodes.filter((n) => !n.group);
    const pids   = new Set(leaves.map((n) => n.data.project_id));
    setSelectionInfo({ count: leaves.length, projectCount: pids.size });
  }, []);

  // ── submit ────────────────────────────────────────────────────────────────

  const handleSubmit = useCallback(async () => {
    const nodes  = gridRef.current?.api?.getSelectedNodes() ?? [];
    const leaves = nodes.filter((n) => !n.group);
    if (leaves.length === 0) return;

    const selected  = leaves.map((n) => n.data);
    
    // Group by project + collect sales_unit_id and project_status_name per project in single pass
    const { byProject, salesUnitByProject, projectStatusByProject } = selected.reduce(
      (acc, b) => {
        if (!acc.byProject[b.project_id]) {
          acc.byProject[b.project_id] = [];
          acc.salesUnitByProject[b.project_id] = b.sales_unit_id;
          acc.projectStatusByProject[b.project_id] = b.project_status_name;
        }
        acc.byProject[b.project_id].push(b);
        return acc;
      },
      { byProject: {}, salesUnitByProject: {}, projectStatusByProject: {} }
    );

    const existingByProject = (salesState.allData?.data || []).reduce((acc, s) => {
      if (s.project?.id) acc[s.project.id] = s;
      return acc;
    }, {});

    setSubmitting(true);
    try {
      // One Sale record per project — buildings are the JSONB array on that record.
      // If a Sale already exists for this project+period, merge new buildings in (skip duplicates).
      // If not, create a new Sale with deal_id + period + all selected buildings.
      const ops = Object.entries(byProject).map(([projectId, buildings]) => {
        const incoming = buildings.map((b) => ({
          id:     b.id,
          name:   b.name,
          weight: b.est_released_weight,
          status: b.building_status,
          remark: '',
          month:  null,
        }));

        const existing = existingByProject[projectId];
        if (existing) {
          const existingIds     = new Set((existing.buildings || []).map((eb) => eb.id));
          const toAdd           = incoming.filter((b) => !existingIds.has(b.id));
          if (toAdd.length === 0) return null;                          // all already present
          const mergedBuildings = [...(existing.buildings || []), ...toAdd];
          return dispatch(updateSalesForecast({
            saleId: existing.id,
            data:   { buildings: mergedBuildings },
          }));
        }

        // New record — deal_id is sent by the thunk, period auto-set by model if omitted
        return dispatch(createSalesForecast({
          data: { 
            period,
            deal_id: projectId,
            sales_unit_id: salesUnitByProject[projectId],
            project_status_name: existing?.project_status_name || projectStatusByProject[projectId],
            buildings: incoming
          },
        }));
      });

      await Promise.all(ops.filter(Boolean));
      onSuccess?.();
      onHide();
    } finally {
      setSubmitting(false);
    }
  }, [dispatch, salesState.allData, onHide, onSuccess, period]);

  if (!show) return null;

  // ── portal — escapes all stacking contexts, z-index via inline style ──────
  return ReactDOM.createPortal(
    <div
      className="tw-fixed tw-inset-0 tw-flex tw-items-center tw-justify-center tw-p-4 tw-bg-black tw-bg-opacity-50"
      style={{ zIndex: 99999 }}              // tw-z-50 is only 50 — not enough
    >
      <div
        className="tw-bg-white tw-rounded tw-shadow-2xl tw-w-full tw-flex tw-flex-col tw-overflow-hidden"
        style={{ maxWidth: 1100, height: '85vh' }}   // arbitrary values kept as inline
        onClick={(e) => e.stopPropagation()}
      >

        {/* ── Header ── */}
        <div className="tw-px-5 tw-py-3.5 tw-bg-slate-50 tw-border-b tw-border-slate-200 tw-flex tw-items-center tw-justify-between tw-shrink-0">
          <span className="tw-font-semibold tw-text-sm tw-text-slate-800">
            Select Buildings for Forecast
          </span>
          <button
            onClick={onHide}
            className="tw-bg-transparent tw-border-0 tw-cursor-pointer tw-text-slate-400 hover:tw-text-slate-600 tw-flex tw-items-center tw-p-1 tw-rounded-sm hover:tw-bg-slate-100"
            aria-label="Close"
          >
            <FaTimes size={13} />
          </button>
        </div>

        {/* ── Filter bar ── */}
        <div className="tw-px-5 tw-py-2.5 tw-border-b tw-border-slate-200 tw-flex tw-gap-2 tw-items-center tw-shrink-0">

          {/* Period picker + hint */}
          {(() => {
            const effectivePeriod = getEffectivePeriod();
            const today           = dayjs();
            const isGrace         = today.date() <= GRACE_DAYS;
            const graceTip        = isGrace
              ? `Grace period (days 1–${GRACE_DAYS}): still reporting for ${effectivePeriod}. Period will advance to ${today.format('MMM-YYYY')} after day ${GRACE_DAYS}.`
              : `Reporting period: ${effectivePeriod}. Changes on the 1st of next month (${today.add(1,'month').format('MMM-YYYY')}).`;

            // The forecast covers the 3 months FOLLOWING the selected period.
            //   e.g. period Jun → forecast Jul, Aug, Sep
            //        period Jul → forecast Aug, Sep, Oct
            const periodBase     = dayjs(period, 'MMM-YYYY');
            const base           = periodBase.isValid() ? periodBase : dayjs(effectivePeriod, 'MMM-YYYY');
            const forecastMonths = [1, 2, 3].map((n) => base.add(n, 'month'));
            const forecastTip    = `Selecting ${base.format('MMM-YYYY')} forecasts the next 3 months: ${forecastMonths.map((m) => m.format('MMM-YYYY')).join(', ')}.`;

            return (
              <div className="tw-flex tw-items-center tw-gap-1.5 tw-shrink-0">
                <div className="tw-flex tw-flex-col tw-gap-1">
                  <div className="tw-flex tw-items-center tw-gap-1.5">
                    <span className="tw-text-xs tw-font-semibold tw-text-slate-600">Period:</span>
                    <MonthYearPicker
                      value={period}
                      onChange={setPeriod}
                      clearable={false}
                      minWidth={140}
                      minDate={effectivePeriod}
                      maxDate={effectivePeriod}
                    />
                  </div>
                  <div className="tw-flex tw-items-center tw-gap-1.5 tw-flex-wrap">
                    <span
                      className={`tw-text-[10px] tw-leading-tight ${isGrace ? 'tw-text-amber-600' : 'tw-text-slate-500'}`}
                      title={graceTip}
                    >
                      {isGrace
                        ? `⚠ Grace — reporting ${effectivePeriod} (unlocks ${today.format('MMM')} after day ${GRACE_DAYS})`
                        : `Reporting ${effectivePeriod}`
                      }
                    </span>
                    <span className="tw-text-slate-300">·</span>
                    <span className="tw-text-[10px] tw-text-slate-500 tw-leading-tight" title={forecastTip}>
                      Forecast
                    </span>
                    {forecastMonths.map((m) => (
                      <span
                        key={m.format('MMM-YYYY')}
                        className="tw-text-[10px] tw-font-semibold tw-text-sky-700 tw-bg-sky-50 tw-border tw-border-sky-200 tw-rounded-sm tw-px-1.5 tw-py-px tw-leading-tight"
                      >
                        {m.format('MMM')}
                      </span>
                    ))}
                  </div>
                </div>
              </div>
            );
          })()}

          <div className="tw-w-px tw-h-5 tw-bg-slate-200 tw-shrink-0" />

          {/* General search — Project, Quote, Job */}
          <div
            className="tw-flex tw-items-center tw-border tw-border-slate-300 tw-rounded-md tw-bg-slate-50 tw-flex-1"
          >
            <span className="tw-px-2 tw-text-slate-400"><FaSearch size={11} /></span>
            <input
              type="text"
              placeholder="Search for Project name, Quote or Job number…"
              value={generalFilter}
              onChange={(e) => setGeneralFilter(e.target.value)}
              className="tw-flex-1 tw-border-0 tw-outline-none tw-bg-transparent tw-text-sm tw-py-1.5 tw-pr-2 tw-text-slate-700 placeholder:tw-text-slate-400"
            />
            {generalFilter && (
              <button
                onClick={() => setGeneralFilter('')}
                className="tw-bg-transparent tw-border-0 tw-cursor-pointer tw-text-slate-400 hover:tw-text-slate-600 tw-px-1.5"
              >
                <FaTimes size={9} />
              </button>
            )}
          </div>

          <span className="tw-ml-auto tw-text-xs tw-text-slate-400">
            {isFetching ? (
              <span className="tw-flex tw-items-center tw-gap-1 tw-text-blue-500">
                <LoadingSpinner size="sm" className="tw-py-0" /> Searching…
              </span>
            ) : (
              `${totalRows} building(s)`
            )}
          </span>
        </div>

        {/* ── Grid ── */}
        <div className="tw-flex-1 tw-min-h-0 tw-px-5 tw-pb-2 tw-relative">
          {isFetching && <LoadingSpinner overlay message="Loading buildings…" />}
          <div className="ag-theme-quartz tw-h-full">
            <AgGridReact
              ref={gridRef}
              rowData={rowData}
              columnDefs={columnDefs}
              autoGroupColumnDef={autoGroupColumnDef}
              rowSelection="multiple"
              groupSelectsChildren
              groupDisplayType="singleColumn"
              groupDefaultExpanded={-1}
              suppressRowClickSelection
              suppressAggFuncInHeader
              animateRows
              suppressMultiSort
              initialGroupOrderComparator={initialGroupOrderComparator}
              onSortChanged={handleSortChanged}
              onSelectionChanged={handleSelectionChanged}
              getRowId={(p) => String(p.data.id)}
              overlayLoadingTemplate='<span class="ag-overlay-loading-center">Loading buildings…</span>'
              overlayNoRowsTemplate='<span style="color:#94a3b8;font-size:13px">No eligible buildings found</span>'
            />
          </div>
        </div>

        {/* ── Pagination ── */}
        <div className="tw-px-5 tw-py-2 tw-border-y tw-border-slate-200 tw-shrink-0 tw-bg-gray-50">
          <Pagination
            currentPage={page}
            totalPages={totalPages}
            totalResults={totalRows}
            perPage={perPage}
            onPageChange={setPage}
            onPageSizeChange={setPerPage}
            pageSizeOptions={PAGE_SIZE_OPTIONS}
            disabled={isFetching}
          />
        </div>

        {/* ── Footer ── */}
        <div className="tw-px-5 tw-py-3 tw-bg-slate-50 tw-border-t tw-border-slate-200 tw-flex tw-items-center tw-justify-between tw-shrink-0">
          <small className="tw-text-xs tw-text-slate-500">
            {selectionInfo.count} building(s) selected across {selectionInfo.projectCount} project(s)
          </small>

          <div className="tw-flex tw-gap-2">
            <Button variant="secondary" onClick={onHide} disabled={submitting}>
              Cancel
            </Button>
            <Button
              variant="primary"
              onClick={handleSubmit}
              loading={submitting}
              disabled={selectionInfo.count === 0}
            >
              Add to Forecast
            </Button>
          </div>
        </div>

      </div>
    </div>,
    document.body
  );
};

SalesBuildingSelectModal.propTypes = {
  show:          PropTypes.bool.isRequired,
  onHide:        PropTypes.func.isRequired,
  onSuccess:     PropTypes.func,
  defaultPeriod: PropTypes.string,
};

SalesBuildingSelectModal.defaultProps = {
  onSuccess:     null,
  defaultPeriod: null,
};

export default SalesBuildingSelectModal;
